import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, Users, Columns3, Compass, UsersRound, Mail, Settings, LogOut, Menu, X, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { useT } from "@/i18n";
import { Wordmark, BrandMark } from "@/components/Brand";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { Footer } from "@/components/Footer";
import { cn } from "@/lib/utils";

const NAV = [
    { to: "/app", label: "Inicio", icon: Home, end: true, testId: "nav-inicio" },
    { to: "/app/contactos", label: "Contactos", icon: Users, testId: "nav-contactos" },
    { to: "/app/pipeline", label: "Pipeline", icon: Columns3, testId: "nav-pipeline" },
    { to: "/app/ruta", label: "Mi ruta", icon: Compass, testId: "nav-ruta" },
    { to: "/app/equipo", label: "Equipo", icon: UsersRound, testId: "nav-equipo" },
    { to: "/app/correos", label: "Correos", icon: Mail, testId: "nav-correos" },
    { to: "/app/ajustes", label: "Ajustes", icon: Settings, testId: "nav-ajustes" },
];

function NavItems({ onNavigate }) {
    const { anyDevEnabled } = useConfig();
    const { t } = useT();
    return (
        <nav className="flex flex-col gap-1">
            {NAV.map(({ to, label, icon: Icon, end, testId }) => (
                <NavLink
                    key={to}
                    to={to}
                    end={end}
                    onClick={onNavigate}
                    data-testid={testId}
                    className={({ isActive }) =>
                        cn(
                            "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-150",
                            isActive
                                ? "bg-nm-accent-soft text-nm-accent"
                                : "text-nm-muted hover:bg-nm-accent-soft/60 hover:text-nm-ink"
                        )
                    }
                >
                    <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
                    {label}
                </NavLink>
            ))}
            {/* Capa de Desarrollo: sólo visible cuando hay algún módulo activado. */}
            {anyDevEnabled && (
                <NavLink
                    to="/app/desarrollo"
                    onClick={onNavigate}
                    data-testid="nav-desarrollo"
                    className={({ isActive }) =>
                        cn(
                            "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-150",
                            isActive
                                ? "bg-nm-accent-soft text-nm-accent"
                                : "text-nm-muted hover:bg-nm-accent-soft/60 hover:text-nm-ink"
                        )
                    }
                >
                    <Sparkles className="h-[18px] w-[18px]" strokeWidth={2} />
                    {t("dev.nav")}
                </NavLink>
            )}
        </nav>
    );
}

export function AppLayout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const doLogout = async () => {
        await logout();
        navigate("/entrar");
    };

    return (
        <div className="flex min-h-screen bg-nm-bg">
            {/* Barra lateral · escritorio */}
            <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-nm-line bg-nm-surface px-4 py-6 lg:flex">
                <div className="px-2">
                    <NavLink to="/app" data-testid="sidebar-logo">
                        <Wordmark />
                    </NavLink>
                </div>
                <div className="mt-8 flex-1">
                    <NavItems />
                </div>
                <div className="border-t border-nm-line pt-4">
                    <div className="flex items-center gap-3 px-2 py-2">
                        <InitialsAvatar name={user?.name} size="sm" />
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-nm-ink">{user?.name}</p>
                            <p className="truncate text-xs text-nm-muted">{user?.email}</p>
                        </div>
                    </div>
                    <button
                        onClick={doLogout}
                        data-testid="logout-button"
                        className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-nm-muted transition-colors duration-150 hover:bg-nm-accent-soft/60 hover:text-nm-ink"
                    >
                        <LogOut className="h-[18px] w-[18px]" strokeWidth={2} />
                        Cerrar sesión
                    </button>
                </div>
            </aside>

            {/* Cabecera · móvil */}
            <header className="fixed inset-x-0 top-0 z-30 flex items-center justify-between border-b border-nm-line bg-nm-surface px-4 py-3 lg:hidden">
                <NavLink to="/app">
                    <Wordmark />
                </NavLink>
                <button
                    onClick={() => setOpen(true)}
                    data-testid="mobile-menu-open"
                    className="grid h-10 w-10 place-items-center rounded-xl border border-nm-line text-nm-ink"
                    aria-label="Abrir menú"
                >
                    <Menu className="h-5 w-5" />
                </button>
            </header>

            {/* Menú desplegable · móvil */}
            {open && (
                <div className="fixed inset-0 z-40 lg:hidden">
                    <div className="absolute inset-0 bg-nm-panel/40" onClick={() => setOpen(false)} />
                    <div className="absolute inset-y-0 right-0 flex w-72 max-w-[85%] flex-col bg-nm-surface px-4 py-6 animate-fade-up">
                        <div className="flex items-center justify-between px-2">
                            <BrandMark />
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="mobile-menu-close"
                                className="grid h-9 w-9 place-items-center rounded-lg text-nm-muted"
                                aria-label="Cerrar menú"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="mt-6 flex-1">
                            <NavItems onNavigate={() => setOpen(false)} />
                        </div>
                        <div className="border-t border-nm-line pt-4">
                            <div className="flex items-center gap-3 px-2 py-2">
                                <InitialsAvatar name={user?.name} size="sm" />
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-nm-ink">{user?.name}</p>
                                    <p className="truncate text-xs text-nm-muted">{user?.email}</p>
                                </div>
                            </div>
                            <button
                                onClick={doLogout}
                                className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-nm-muted hover:text-nm-ink"
                            >
                                <LogOut className="h-[18px] w-[18px]" /> Cerrar sesión
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Contenido */}
            <div className="flex min-h-screen flex-1 flex-col lg:pl-64">
                <main className="flex-1 px-4 pb-16 pt-20 sm:px-6 lg:px-10 lg:pt-10">
                    <div className="mx-auto w-full max-w-5xl">{children}</div>
                </main>
                <Footer />
            </div>
        </div>
    );
}
